import React from 'react';
import { Link } from 'react-router-dom';
import SitePage, { SectionHeader, CTABand } from './SiteChrome';

// Film (/film): the studio's other practice — film finance, deal structuring,
// and production. Same editorial system as /services (ac-svc spreads, ghost
// numerals, drawn glyphs, timeline), its own imagery and copy.

/* Local glyphs, same drawn-line language as the SiteChrome library. */
const glyphProps = {
  viewBox: '0 0 48 48', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true,
};
const FILM_GLYPHS = {
  // Finance: the stack, rising — three columns and the coin that tops them
  finance: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <path d="M10 40 L10 30 M10 40 L38 40" style={{ '--len': 40 }} />
      <path d="M19 40 L19 24" style={{ '--len': 16, '--d': '0.35s' }} />
      <path d="M28 40 L28 18" style={{ '--len': 22, '--d': '0.55s' }} />
      <circle cx="28" cy="11" r="4" style={{ '--len': 26, '--d': '0.8s' }} />
    </svg>
  ),
  // Deals: the waterfall — money stepping down through the recoupment order
  deals: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <path d="M8 12 L20 12 L20 22 L32 22 L32 32 L44 32" style={{ '--len': 70 }} />
      <path d="M38 38 L44 32 L38 26" style={{ '--len': 18, '--d': '0.6s' }} />
      <path d="M8 18 Q11 16 14 18" style={{ '--len': 8, '--d': '0.85s' }} />
    </svg>
  ),
  // Production: the slate, about to clap
  production: (props) => (
    <svg {...glyphProps} {...props} className={`ac-draw ${props?.className || ''}`}>
      <rect x="8" y="20" width="32" height="18" style={{ '--len': 100 }} />
      <path d="M8 20 L36 10 L40 17 L12 27" style={{ '--len': 66, '--d': '0.4s' }} />
      <path d="M14 18 L18 12 M22 16 L26 10 M30 13 L34 8" style={{ '--len': 24, '--d': '0.75s' }} />
    </svg>
  ),
};

const PRACTICES = [
  {
    anchor: 'finance',
    img: '/ext/media/film-finance.jpg',
    imgAlt: 'Production budget ledger and confidential term sheet with turquoise ink, Aegean light',
    name: 'Film finance',
    promise: 'The capital stack, assembled so it closes.',
    deliver: 'A finance plan built around the project — equity, pre-sales and minimum guarantees, tax incentives and rebates across jurisdictions, gap and bridge only where they earn their place; investor materials and cash-flow schedules a lender can actually read; incentive mapping so no soft money is left on the table; coordination with banks, the bond company, and the collection account through close.',
    not: 'A fund, a loan, or a securities offering. We structure and assemble; licensed counsel and tax advisors stay in the room, and the money always belongs to the production.',
    start: 'A packaged project — script, budget, schedule, key attachments — that needs its financing closed. Or a stack that\u2019s been stuck at seventy percent for a year.',
  },
  {
    anchor: 'deals',
    img: '/ext/media/film-deal.jpg',
    imgAlt: 'Contract pages with a hand-drawn recoupment waterfall in turquoise ink, espresso cups, wax-sealed envelope',
    name: 'Deal structuring',
    promise: 'Terms that survive delivery.',
    deliver: 'Recoupment waterfalls modeled before anyone signs, so every party sees the same picture of who gets paid when; term sheets for equity, debt, and talent; co-production structures across borders and treaties; sales, distribution, and streamer deal review — what the backend definition actually pays, not what it implies; collection account setup so the waterfall runs itself.',
    not: 'Legal drafting — counsel papers the deal. We structure the economics so there\u2019s something worth papering.',
    start: 'A term sheet you\u2019ve been asked to sign, a co-production taking shape, or a waterfall no two parties read the same way.',
  },
  {
    anchor: 'production',
    img: '/ext/media/film-production.jpg',
    imgAlt: 'Cinema camera and clapperboard on a whitewashed terrace above turquoise Aegean water',
    name: 'Film production',
    promise: 'Greenlight to delivered picture, minded like the money depends on it — because it does.',
    deliver: 'Development and packaging; budgets and schedules that hold; physical production — crewing, locations, on-set management; post supervision through edit, sound, color, and mix; delivery itself — the full materials list, QC passed, on time to the distributor or platform.',
    not: 'A vanity credit. If we produce, we run the schedule and answer for the budget.',
    start: 'A financed project that needs a producer who has minded both the set and the spreadsheet.',
  },
];

const PHASES = [
  {
    name: 'Package',
    text: 'Script, budget, schedule, key attachments — assembled so a financier can say yes to something concrete. Most projects that never get made were never really packaged.',
  },
  {
    name: 'Close',
    text: 'The stack agreed, the waterfall modeled, the paper signed, the bond in place. Money moves on a schedule everyone has already read.',
  },
  {
    name: 'Shoot',
    text: 'Production run tight to the day and the dollar — a plan built to survive contact with weather, schedules, and luck.',
  },
  {
    name: 'Deliver',
    text: 'Post through QC and the deliverables list. The picture ships, the waterfall starts to run, and the accounting stays legible.',
  },
];

function PracticeBlock({ p, i }) {
  const Glyph = FILM_GLYPHS[p.anchor];
  return (
    <section className="ac-svc" id={p.anchor}>
      <div className="ac-svc-media ac-reveal-wipe">
        <img src={p.img} alt={p.imgAlt} loading="lazy" />
        <span className="ac-ghost-num" aria-hidden="true">0{i + 1}</span>
      </div>
      <div className="ac-svc-copy">
        {Glyph && <Glyph className="ac-svc-glyph" />}
        <h3 className="ac-reveal">{p.name}</h3>
        <p className="ac-svc-promise ac-reveal" style={{ '--d': '0.08s' }}>{p.promise}</p>
        <dl>
          <div className="ac-reveal" style={{ '--d': '0.14s' }}><dt>What we deliver</dt><dd>{p.deliver}</dd></div>
          <div className="ac-reveal" style={{ '--d': '0.2s' }}><dt>What it is not</dt><dd>{p.not}</dd></div>
          <div className="ac-reveal" style={{ '--d': '0.26s' }}><dt>Typical start point</dt><dd>{p.start}</dd></div>
        </dl>
        <p className="ac-reveal" style={{ '--d': '0.3s' }}>
          <Link className="ac-textlink" to="/contact?interest=film">Discuss this with us →</Link>
        </p>
      </div>
    </section>
  );
}

export default function Film() {
  return (
    <SitePage cta={false}>
      <section className="ac-section" style={{ paddingBottom: 0 }}>
        <div className="ac-wrap">
          <SectionHeader kicker="Film" title="Finance, deals, production." />
          <p className="ac-lead ac-reveal" style={{ maxWidth: 640 }}>
            The studio&rsquo;s other practice: putting pictures together. The money,
            the paper, and the production itself — handled by someone who has sat
            on every side of the table, and reads a recoupment schedule the way
            most people read a call sheet.
          </p>
        </div>
      </section>

      <div className="ac-wrap">
        {PRACTICES.map((p, i) => (
          <PracticeBlock p={p} i={i} key={p.anchor} />
        ))}
      </div>

      {/* How a picture gets made — same drawn timeline as /approach */}
      <section className="ac-section" id="phases">
        <div className="ac-lightwash" aria-hidden="true" />
        <div className="ac-wrap" style={{ position: 'relative' }}>
          <SectionHeader kicker="The shape of it" title="How a picture gets made." />
          <div className="ac-timeline">
            {PHASES.map((ph) => (
              <div className="ac-tstep" key={ph.name}>
                <h3 className="ac-reveal">{ph.name}<em>.</em></h3>
                <p className="ac-reveal" style={{ '--d': '0.12s' }}>{ph.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagements — bespoke by nature; no rate card theater */}
      <section className="ac-section ac-section-wash" id="engage">
        <div className="ac-wrap">
          <SectionHeader kicker="Engagements" title="Structured per production." />
          <div className="ac-engage">
            <div className="ac-engage-card ac-reveal">
              <h3>Advisory<em>.</em></h3>
              <p>The stack, the waterfall, or a deal on the table — reviewed and structured. Scoped as consulting or a flat fee, defined before we start.</p>
            </div>
            <div className="ac-engage-card ac-reveal" style={{ '--d': '0.12s' }}>
              <h3>Producing<em>.</em></h3>
              <p>The full engagement: packaging, finance, physical production, delivery. Producer terms — fees and points — set project by project.</p>
            </div>
          </div>
          <p style={{ marginTop: 40 }}>
            <Link className="ac-textlink" to="/contact?interest=film">Tell us what you&rsquo;re making →</Link>
          </p>
        </div>
      </section>

      <CTABand line={'If the picture deserves to get made — that\u2019s the work.'} />
    </SitePage>
  );
}
